/**
 * useDesktopSession — everything /desktop does, shared by today's page and the
 * rebuilt one (design record §7b rule 1, PV15 part 1, 2026-09-30).
 *
 * Moved here word for word from src/pages/AgentActivity.tsx, so the two pages
 * can never answer the embedded avatar page, a wake, a queue ticket or a
 * returning tab differently. The page keeps only what it draws. The tests that
 * pin the shell's behaviour read this file together with the page.
 */
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import type { PendingBrowserLaneSignIn } from '../services/composio/browserLaneRoster';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { collection, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { ACCOUNT_FIRESTORE_PREFIX } from '../services/prest0nRuntime/constants';
import { getUserUiLanguage } from '../services/threadMetadata/uiLanguage';
import { PREST0_INC_ACCOUNT_ID } from '../config/internalAccess';
import {
  useRealtimeDesktopTarget,
  type RealtimeDesktopTarget,
} from './useRealtimeDesktopTarget';
import { frameAddress } from './realtimeDesktopUrl';
import { useRbac } from './useRbac';
import AvatarBridge from '../services/avatarBridge/AvatarBridge';
import { bootPollDelayMs } from '../utils/bootPollCadence';
import {
  fetchWorkspaceBootStatus,
  knownVoiceTransport,
  onVoiceTransportChange,
} from '../services/browserService/workspaceBootGate';
import { readBrowserServiceKeyFresh } from '../services/browserService/baseUrl';
import { decidePageCredential } from '../services/browserService/desktopPageCredential';
import { vetWorkstationSvc } from '../services/browserService/workstationSvc';
import { readDesktopWindowOpen, requestRunLiveView } from '../services/composio/runLiveViewEvents';
import { DESKTOP_SESSION_ID_RE, WORKSTATION_SVC_RE } from '../services/browserService/desktopSessionHref';
import {
  DESKTOP_SHOW_SESSION_EVENT,
  announceDesktopShowAnswer,
  onDifferentMachines,
  type DesktopShowSessionDetail,
} from '../services/browserService/desktopShowSession';
import { ownDesktopIdsOn } from '../services/browserService/ownDesktops';
import { programWorkstationEntryOf, useWorkstationFacts, type ProgramWorkstationEntry } from '../services/programWorkstation/useWorkstationFacts';
import { openProgramWorkstation } from '../services/programWorkstation/openProgramWorkstation';
import { chatBelowFrame, requestChatBelow, useChatBelowShown } from '../components/GlobalChatSystem/chatBelow';
import { useLiveMeter } from './useLiveMeter';
import type { LiveMeterView } from './liveMeterModel';

// ── Which voice server the avatar page should open its socket to (avatar
// experience PR-B, 2026-09-20). The page cannot read ops/*; the boot-status
// endpoint answers `voice: {transport, url}` (the workstation, or the platform-
// hosted gateway once the account rides it), and the shell hands the URL to
// the page WITH the ID token.
//
// Since 2026-10-08 the fact is the one the /desktop boot poll already brought
// back (workspaceBootGate.ts remembers every `ready` answer), so the first
// voice sign-in carries it without a fetch of its own. A page opened without a
// boot poll gets one fetch, bounded well inside the page's own wait; and when
// the fact arrives or changes after a sign-in went out, the page is handed a
// fresh one (see onVoiceTransportChange below), so a slow first answer can no
// longer pin the person to the workstation for the token's whole life.
const VOICE_TRANSPORT_TTL_MS = 5 * 60 * 1000;
const VOICE_TRANSPORT_WAIT_MS = 4000;
/** The page's credential never holds the voice sign-in longer than this. */
const VOICE_CREDENTIAL_WAIT_MS = 4000;
/** The URL if it is known within the wait; null otherwise (a late answer re-hands the page). */
function voiceUrlWithin(accountId: string, waitMs: number): Promise<string | null> {
  const known = knownVoiceTransport(accountId);
  if (known) {
    // Old enough to re-check: ask again in the background; a change re-hands the page.
    if (Date.now() - known.atMs > VOICE_TRANSPORT_TTL_MS) void fetchWorkspaceBootStatus(accountId);
    return Promise.resolve(known.url);
  }
  return Promise.race([
    fetchWorkspaceBootStatus(accountId).then(() => knownVoiceTransport(accountId)?.url ?? null, () => null),
    new Promise<string | null>((resolve) => setTimeout(() => resolve(knownVoiceTransport(accountId)?.url ?? null), waitMs)),
  ]);
}
/** A value within the wait, or the fallback — never a stalled or failed sign-in. */
function withinOr<T>(work: Promise<T>, waitMs: number, fallback: T): Promise<T> {
  return Promise.race([
    work.catch(() => fallback),
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), waitMs)),
  ]);
}

/**
 * Extract origin (scheme+host) from a URL string. Used to derive the
 * AvatarBridge allowed-origins list from the avatar VM's baseUrl.
 *
 * Examples:
 *   'https://35-238-149-88.sslip.io'         → 'https://35-238-149-88.sslip.io'
 *   'https://35-238-149-88.sslip.io/foo'   → 'https://35-238-149-88.sslip.io'
 *   'not-a-url'                             → null
 */
/** Where the embedded page's machine key lives (2026-09-24): the shared internal key for the
 *  internal machine, otherwise the account's own vm record. */
function bsKeyTarget(
  target: Pick<RealtimeDesktopTarget, 'source' | 'accountId'>
): { kind: 'internal' } | { kind: 'account'; accountId: string } {
  if (target.source === 'internal') return { kind: 'internal' };
  return { kind: 'account', accountId: target.accountId ?? '' };
}

/** How often the shell hands the page its credential again (workstationTickets.ts DESKTOP_PAGE_REHAND_MS). */
const DESKTOP_PAGE_REHAND_MS = 4 * 60_000;

/** The signed-in person, or null; a ticket held for anyone else is never handed to the page. */
function signedInUid(): string | null {
  try {
    return getAuth().currentUser?.uid ?? null;
  } catch {
    return null;
  }
}

/**
 * What the embedded page reaches its workstation with (S1b-2c step 2,
 * 2026-09-25): a personal ticket that opens only this person's own desktops,
 * when the machine says it takes one; else the machine key, as before; and
 * since 2026-10-04, with no key at all, a ticket anyway
 * (desktopPageCredential.ts, the one rule). The ticket module is loaded on
 * first use, so none of it joins the first download.
 */
async function credentialForPage(
  target: Pick<RealtimeDesktopTarget, 'source' | 'accountId' | 'baseUrl' | 'browserServiceToken'>
): Promise<string | null> {
  const accountId = target.source === 'internal' ? PREST0_INC_ACCOUNT_ID : target.accountId;
  const host = target.baseUrl && accountId ? `${target.baseUrl.replace(/\/+$/, '')}/browser-api` : null;
  const uid = signedInUid();
  return decidePageCredential({
    ticket: async (noKey) => {
      if (!host || !accountId) return null;
      return await import('../services/browserService/workstationTickets')
        .then((m) => m.ticketForDesktopPage(host, accountId, { uid, noKey }))
        .catch(() => null);
    },
    key: async () => {
      return (await readBrowserServiceKeyFresh(bsKeyTarget(target))) ?? target.browserServiceToken ?? null;
    },
  });
}

function toOrigin(url: string): string | null {
  try {
    const u = new URL(url);
    // URL.origin includes the port only when non-default (443 for https),
    // which is exactly the format window.postMessage's event.origin uses.
    return u.origin || null;
  } catch {
    return null;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function describeSource(target: RealtimeDesktopTarget): string {
  if (target.source === 'internal') return 'Internal Prest0n VM';
  if (target.source === 'account-vm' && target.provisioning) {
    return 'workstation booting…';
  }
  if (target.source === 'account-vm' && target.accountId) {
    // Uid-keyed fallback accounts have a raw Firebase UID as their account
    // id — meaningless (and mildly alarming) in the header. Friendly label
    // instead; named accounts (e.g. dash-separated firm ids) still show.
    if (/^[A-Za-z0-9]{20,}$/.test(target.accountId)) return 'your workspace';
    return `account: ${target.accountId}`;
  }
  return 'Not connected';
}

// Desktop-launch lifecycle states the embedded avatar app reports up via
// postMessage. Kept in lockstep with desktopStatus() in
// functions/src/prest0nVm/bootstrap/public/preston-realtime.html.
export const DESK_STATUS_STATES = ['launching', 'restoring', 'retrying', 'ready', 'failed', 'queued'] as const;
export type DeskStatusState = (typeof DESK_STATUS_STATES)[number];

// copy: desktop_status_chrome
export const DESK_STATUS_COPY: Record<Exclude<DeskStatusState, 'ready'>, string> = {
  launching: 'starting your live desktop…',
  // 2026-09-05 (workstation plan PR-9): every desktop slot on the
  // workstation is a teammate's live session. The embed keeps retrying and
  // takes the first slot that frees; nothing else is blocked meanwhile.
  queued: "someone's using this desktop right now — you're next. chat and everything else work in the meantime.",
  // 2026-09-03: also what the embed's live-view watchdog reports when it is
  // repointing a dropped viewer after a sleep — and it only reports it once
  // a reconnect has taken longer than 1.5s, so this chip means "genuinely
  // taking a moment", never the sub-second recovery of a reopened laptop.
  restoring: 'reconnecting your desktop…',
  retrying: 'the desktop is taking a moment — retrying automatically…',
  failed: "the desktop couldn't start — chat and everything else still work.",
};

/** A queue ticket for a desktop slot, as the page shows it (the viewer's own). */
export interface DesktopQueueTicket {
  status: 'queued' | 'summoned';
  position: number | null;
  etaSeconds: number | null;
  scaling: { action: 'waking' | 'minting'; etaSeconds: number | null } | null;
}

/** What the page draws from. */
export interface DesktopSession {
  target: RealtimeDesktopTarget;
  /** The embed's address, carrying a validated `?session=` when the link named one. */
  embedSrc: string | null;
  /** Bumped to reload the embed. */
  iframeKey: number;
  bindIframe: (el: HTMLIFrameElement | null) => void;
  /** The embed's onLoad: marks it loaded and says hello to the page. */
  onFrameLoad: () => void;
  /** The stage's box, measured to fill the screen. */
  frameRef: React.MutableRefObject<HTMLDivElement | null>;
  stageHeight: number | null;
  /** The embed's own content height on portrait phones, else null. */
  embeddedHeight: number | null;
  /**
   * Prest0n's transcript stands under the frame (Justin, 2026-10-17; chatBelow.ts): the frame is the screen (16:9 of
   * its width, never under its floor) and its foot, with no floor of the frame's. Optional: a stand-in session without
   * it draws the frame as before.
   */
  chatBelow?: boolean;
  deskStatus: Exclude<DeskStatusState, 'ready'> | null;
  poolStrip: { total: number; up: number; waking: number } | null;
  signInOwed: PendingBrowserLaneSignIn | null;
  handleResumeSignIn: () => void;
  /** The workspace's Windows workstation for a desktop program (LexPI), once it has one and no sign-in is owed. */
  programWorkstation: ProgramWorkstationEntry | null;
  /** Opens the workstation window: the same one the Legal apps rows, onboarding and the chat's card open. */
  handleOpenWorkstation: () => void;
  /**
   * The live meter (usage-metering step 11, liveMeterModel.ts): the person's conversation with Prest0n and the
   * workstations' awake time, or null when there is nothing to show. Optional: a stand-in session without it draws no chip.
   */
  liveMeter?: LiveMeterView | null;
  queueTicket: DesktopQueueTicket | null;
  desktopHold: boolean;
  bootElapsedMin: number;
  bootStalled: boolean;
  bootStallReason: string | null;
  desktopWarming: boolean;
  frameStalled: boolean;
  wakeBusy: boolean;
  requestWake: (source: 'desktop' | 'button') => Promise<void>;
  handleRetry: () => void;
  handleFrameRetry: () => void;
}

export function useDesktopSession(): DesktopSession {
  // ── Per-session service-base override (2026-08-18) ────────────────────
  // Sign-in desktop sessions can live on the user's ACCOUNT VM instead of
  // the platform-shared service; their links carry `svc=<base>`. Set
  // the runtime override (which every browser-service base resolver already
  // honors) before the live view mounts, so
  // frames/keepalive/noVNC all target the VM that actually hosts the
  // session. Validated by vetWorkstationSvc: the shape (https, a dashed-sslip
  // host, a known service path) AND a host that is one of THIS person's own
  // workstations (S1b-3a, 2026-09-26). The shape alone admitted any address on
  // the internet — sslip.io resolves them all — so a crafted link could point
  // the sign-in window at someone else's machine. Anything else is ignored.
  useEffect(() => {
    let cancelled = false;
    let svc = '';
    try {
      svc = (new URLSearchParams(window.location.search).get('svc') || '').trim();
    } catch { /* malformed query — shared base applies */ }
    if (!svc) return undefined;
    void vetWorkstationSvc(svc).then((base) => {
      if (cancelled || !base) return;
      (window as unknown as { PREST0_BROWSER_SERVICE_BASE_URL?: string })
        .PREST0_BROWSER_SERVICE_BASE_URL = base;
    });
    return () => { cancelled = true; };
  }, []);

  // A link's token authorizes nothing. The signed deep link was retired on 2026-10-06; the page's own-session rule
  // and its vetted workstation address are the checks. Old links still carry a token, so it leaves the address bar:
  // an address copied from it then carries nothing that looks like a credential. Everything else in it (the session,
  // the workstation) stays.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') ?? params.get('t');
    if (!token) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('t');
      url.searchParams.delete('token');
      // Not `next`: check-desktop-page.mjs reads the first `const next =` in this file as the frame's height rule.
      const cleaned = `${url.pathname}${url.search}${url.hash}`;
      if (cleaned !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        window.history.replaceState(window.history.state, '', cleaned);
      }
    } catch { /* the token stays in the bar; nothing reads it */ }
  }, []);

  const target = useRealtimeDesktopTarget();

  // ── "Show me on the desktop page" (2026-09-20, P6b) ───────────────────
  // A computer run's card, the voice hand-off and the sign-in window's "open
  // the live desktop" all name the desktop SESSION the person should be
  // looking at. The signed deep link (retired 2026-10-06) had put `?session=`
  // in the URL since the day it shipped and this page threw it away. Validated to the browser-service's own id charset and
  // handed to the embed on its URL, where the page attaches to it at boot
  // instead of launching its home page over it — and REFUSES it there if the
  // session belongs to someone else (the page verifies the owner). Absent ⇒
  // the URL, and the page, are byte-identical to before.
  //
  // State since 2026-10-06 (PR 4 of the DocuSign fix): the sign-in window's
  // "open the live desktop" can ask a page already on screen to show its
  // session (below), and react-router keeps this page mounted when only the
  // address changes, so the address alone would never reach it.
  const [requestedSessionId, setRequestedSessionId] = useState<string | null>(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('session') || '';
      return /^desktop-[a-z0-9-]{6,64}$/i.test(raw) ? raw : null;
    } catch {
      return null;
    }
  });
  // The workstation that session lives on, when the link says (`svc=`). A
  // session on another machine than the one this page shows is never handed to
  // the embed: it would not find it there, and would launch its home page over
  // the person's browser instead. The shape is enough here: this value can only
  // keep a session off the embed, never move the embed (that is the vetted
  // override above).
  const [sessionMachine, setSessionMachine] = useState<string | null>(() => {
    try {
      const raw = (new URLSearchParams(window.location.search).get('svc') || '').trim().replace(/\/+$/, '');
      return WORKSTATION_SVC_RE.test(raw) ? raw : null;
    } catch {
      return null;
    }
  });
  const embedSrc = useMemo(() => {
    if (!target.targetUrl) return null;
    if (!requestedSessionId) return target.targetUrl;
    if (onDifferentMachines(sessionMachine, target.baseUrl)) return target.targetUrl;
    const joiner = target.targetUrl.includes('?') ? '&' : '?';
    return `${target.targetUrl}${joiner}session=${encodeURIComponent(requestedSessionId)}`;
  }, [target.targetUrl, target.baseUrl, requestedSessionId, sessionMachine]);
  // `/desktop?debug=1` opens the page's own diagnostics overlay (realtimeDesktopUrl.frameAddress).
  const [shellSearch] = useState<string>(() => {
    try {
      return window.location.search;
    } catch {
      return '';
    }
  });
  const frameSrc = useMemo(() => frameAddress(embedSrc, shellSearch), [embedSrc, shellSearch]);

  // A session on another workstation says so, once, when this visit's own
  // resolve has landed (a warm target can be a machine that has since moved):
  // the sign-in window that sent the person here comes back and says why.
  const announcedOtherMachineRef = useRef<string | null>(null);
  useEffect(() => {
    if (!requestedSessionId || target.warm || !onDifferentMachines(sessionMachine, target.baseUrl)) return;
    if (announcedOtherMachineRef.current === requestedSessionId) return;
    announcedOtherMachineRef.current = requestedSessionId;
    announceDesktopShowAnswer({ sessionId: requestedSessionId, answer: 'other-machine' });
  }, [requestedSessionId, sessionMachine, target.baseUrl, target.warm]);


  // Bump key on retry to force iframe reload.
  const [iframeKey, setIframeKey] = useState(0);

  // "Open the live desktop" (2026-10-06, PR 4 of the DocuSign fix): the
  // sign-in window asks this page, already on screen, to show its session
  // (desktopShowSession.ts). A session on another workstation is refused, and
  // the window says why. Otherwise the page shows it. Nothing reloads when the
  // embed was given that session already, or when it was given none, reports
  // itself live, and the session is the person's only desktop on that machine
  // (the embed then attached to it by its own boot rule). Anything else loads
  // a fresh embed with the session on its address; any doubt does too.
  const targetRef = useRef(target);
  targetRef.current = target;
  const requestedSessionRef = useRef(requestedSessionId);
  requestedSessionRef.current = requestedSessionId;
  // The embed's last reported desktop state, 'ready' included (deskStatus drops it).
  const lastDeskStateRef = useRef<DeskStatusState | null>(null);
  useEffect(() => {
    let alive = true;
    const onShow = (e: Event) => {
      const detail = (e as CustomEvent<DesktopShowSessionDetail>).detail;
      const id = detail && typeof detail.sessionId === 'string' ? detail.sessionId : '';
      if (!DESKTOP_SESSION_ID_RE.test(id)) return;
      e.preventDefault();
      const base = typeof detail.base === 'string' ? detail.base : null;
      const shown = targetRef.current;
      if (!shown.warm && onDifferentMachines(base, shown.baseUrl)) {
        detail.answer = 'other-machine';
        return;
      }
      detail.answer = 'showing';
      if (requestedSessionRef.current === id) return;
      const uid = signedInUid();
      const embedLive = requestedSessionRef.current === null && lastDeskStateRef.current === 'ready';
      void (embedLive && base && uid ? ownDesktopIdsOn(base, uid) : Promise.resolve(null)).then((ids) => {
        if (!alive || requestedSessionRef.current === id) return;
        if (ids && ids.length === 1 && ids[0] === id) return;
        setSessionMachine(base);
        setRequestedSessionId(id);
        setIframeKey((k) => k + 1);
      });
    };
    document.addEventListener(DESKTOP_SHOW_SESSION_EVENT, onShow);
    return () => {
      alive = false;
      document.removeEventListener(DESKTOP_SHOW_SESSION_EVENT, onShow);
    };
  }, []);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // L0.4 (2026-09-08): the iframe mounts only once the target has resolved (loading /
  // provisioning / queue / hold / stall gates below), while the bridge registration effect used
  // to key on baseUrl + accountId + iframeKey — so an iframe that appeared AFTER those settled
  // was never registered and the allowlist stayed empty. The element itself is now a dependency.
  const [iframeEl, setIframeEl] = useState<HTMLIFrameElement | null>(null);
  const bindIframe = useCallback((el: HTMLIFrameElement | null) => {
    iframeRef.current = el;
    setIframeEl(el);
  }, []);
  const postShellHello = useCallback(() => {
    const origin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    const frameWindow = iframeRef.current?.contentWindow;
    if (!origin || !frameWindow) return;
    try {
      // canOpenWindows (PR-9c): this shell opens a window the page's picker asks for ('openWindow').
      frameWindow.postMessage({ source: 'prest0-shell', type: 'hello', canOpenWindows: true }, origin);
    } catch { /* ignore */ }
  }, [target.baseUrl]);

  // ─────────────────────────────────────────────────────────────────────
  // One-screen stage height (2026-08-28, avatar-desktop redesign).
  //
  // The frame should be exactly the screen minus what is above it and the
  // fixed chat bar below it — no more (dead background above the footer),
  // no less (a cramped live desktop). No calc() can express that: the site
  // nav and the trial banner are IN NORMAL FLOW above this page and their
  // combined height varies by account state, and the docked chat pane
  // narrows the content shell without changing the viewport. So measure.
  //
  // getBoundingClientRect().top is viewport-relative, so scrollY is added
  // back to get a scroll-independent document offset — the value must not
  // change as the user scrolls to the footer.
  //
  // Where the chat cannot dock (2026-10-17, Justin: "below where it can't sit
  // beside"; chatBelow.ts) the same measurement asks for the chat's place
  // under the frame, and while the chat stands there the frame is the screen
  // (16:9 of its width, never under its floor) and its foot.
  // ─────────────────────────────────────────────────────────────────────
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [stageHeight, setStageHeight] = useState<number | null>(null);
  // The frame's height while the chat stands under it, when the screen holds both; else null.
  const [belowHeight, setBelowHeight] = useState<number | null>(null);
  // Read by the measurement, which outlives renders: a page that reports its own height keeps the chat off its frame,
  // and a chat already standing under the frame keeps its place down to its typing least (chatBelow.ts).
  const embeddedHeightRef = useRef<number | null>(null);
  const chatStandingRef = useRef(false);
  const measureRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    // The screen's height at rest: under a finger the tallest seen at this width, so the on-screen keyboard (Android
    // shrinks the window for it) never moves the chat or the frame above it; under a mouse the window's own.
    let rest: { width: number; height: number } | null = null;
    const restHeight = (height: number): number => {
      let finger = false;
      try { finger = window.matchMedia('(pointer: coarse)').matches; } catch { /* no matchMedia: a mouse */ }
      if (!finger) return height;
      if (!rest || rest.width !== window.innerWidth) rest = { width: window.innerWidth, height };
      else rest.height = Math.max(rest.height, height);
      return rest.height;
    };
    const measure = () => {
      const el = frameRef.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      const offsetFromDocTop = box.top + window.scrollY;
      // LAYOUT viewport, deliberately — never visualViewport. offsetFromDocTop
      // above is a layout-viewport measurement, so mixing in a visual-viewport
      // height makes the two disagree exactly when they diverge: an open mobile
      // keyboard or a hidden URL bar would collapse the live desktop to its
      // floor and jitter it on every scroll (2026-08-28 review).
      const vh = window.innerHeight;
      // Clearance for the fixed chat bar. Floor is the platform's own numbers
      // — --prompt-bar-gap (1.5rem) + --prompt-bar-height (56px) in
      // GlobalStyles, plus slack because the bar renders nearer 68px — and the
      // safe-area inset is added in CSS by the Frame's margin-bottom rather
      // than guessed here. Kept as tight as that allows on purpose: this height
      // is what the embed reads to choose its layout, so an over-generous
      // allowance on a 768px laptop pushed it into the layout meant for phones
      // (2026-08-28 review).
      const chatAllowance = Math.min(116, Math.max(96, Math.round(vh * 0.11)));
      const next = Math.round(vh - offsetFromDocTop - chatAllowance);
      // The chat's place where it cannot dock: the screen (16:9 of its width, never under its floor) inside the frame's
      // own padding and foot, the rest of the screen the chat's. The engine decides whether it stands there.
      const screen = el.firstElementChild ? el.firstElementChild.getBoundingClientRect() : null;
      const below = chatBelowFrame({
        screenWidth: screen ? screen.width : el.clientWidth,
        chrome: screen ? box.height - screen.height : 0,
        room: restHeight(vh) - offsetFromDocTop,
        standing: chatStandingRef.current,
      });
      const belowFits = below.fits && embeddedHeightRef.current === null;
      requestChatBelow({ top: Math.round(box.top + below.height), floor: Math.round(box.top), fits: belowFits });
      setBelowHeight(belowFits ? below.height : null);
      // Never below a usable minimum: a very short window scrolls instead of
      // collapsing the live desktop to a sliver.
      setStageHeight((prev) => {
        const clamped = Math.max(320, next);
        // Ignore sub-pixel churn so a resize storm can't loop us.
        return prev !== null && Math.abs(prev - clamped) < 2 ? prev : clamped;
      });
    };
    measureRef.current = measure;
    measure();
    // The banner and nav mount after us; re-measure once they have settled.
    const t1 = setTimeout(measure, 120);
    const t2 = setTimeout(measure, 600);
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure);
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(measure);
      ro.observe(document.documentElement);
    } catch { /* no ResizeObserver — the listeners above still cover resizes */ }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      ro?.disconnect();
      measureRef.current = () => undefined;
      // The page is gone: the chat stands under nothing.
      requestChatBelow(null);
    };
  }, []);

  // Embedded-portrait content height reported by the avatar page via
  // postMessage. Null = desktop/landscape (fixed-height box). A number =
  // grow the box to that height so the whole outer page scrolls as one.
  const [embeddedHeight, setEmbeddedHeight] = useState<number | null>(null);
  embeddedHeightRef.current = embeddedHeight;

  // Desktop-launch lifecycle reported by the embedded avatar app. Null =
  // nothing to show (no report yet, or the desktop is ready).
  const [deskStatus, setDeskStatus] = useState<Exclude<DeskStatusState, 'ready'> | null>(null);

  // ─────────────────────────────────────────────────────────────────────
  // Avatar/Platform Bridge integration (2026-05-23).
  //
  // Register the avatar iframe with the bridge singleton and set the
  // allowed origins for incoming bridge requests to the current user's
  // avatar VM origin. Without this registration the iframe can send
  // bridge requests but responses can't come back (no target window).
  //
  // Per-account scope comes from `target.baseUrl` (resolved by
  // useRealtimeDesktopTarget against the user's Firestore account doc).
  // Internal Prest0 users get the internal VM origin (DEFAULT_INTERNAL_BASE
  // in useRealtimeDesktopTarget). Account users get their own VM URL.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = iframeEl;
    if (!el) {
      AvatarBridge.registerIframe(null);
      AvatarBridge.setAllowedOrigins([]);
      return;
    }
    const origin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    AvatarBridge.registerIframe(el, target.accountId || undefined);
    if (origin) {
      AvatarBridge.setAllowedOrigins([origin]);
      // L0.4: identify ourselves to the page, pinned to the VM origin. The page pins the shell's
      // origin from the first parent message on an allowlisted origin and, until then, can only
      // guess the fixed production hosts (our index.html sends no referrer) — so a preview-channel
      // or localhost shell is found through this hello. Posted again from onLoad in case the
      // frame has not loaded yet.
      try {
        el.contentWindow?.postMessage({ source: 'prest0-shell', type: 'hello', canOpenWindows: true }, origin);
      } catch { /* not loaded yet — onLoad posts again */ }
    } else {
      AvatarBridge.setAllowedOrigins([]);
    }
    return () => {
      AvatarBridge.registerIframe(null);
      AvatarBridge.setAllowedOrigins([]);
    };
  }, [iframeEl, target.baseUrl, target.accountId]);

  // ─────────────────────────────────────────────────────────────────────
  // Embedded-portrait height bridge (2026-07-01).
  //
  // On portrait phones the avatar page stacks the avatar + live desktop
  // taller than one screen and reports its real content height up to us.
  // We grow the Frame/iframe to that height (and drop the overflow clip)
  // so the whole /desktop page scrolls as one, instead of the tall child
  // panning inside a fixed-height box. Origin is validated against the
  // resolved avatar VM origin (same source the AvatarBridge trusts).
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const allowedOrigin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    if (!allowedOrigin) return;
    // The internal target carries the label 'prest0-internal' for logging; the voice
    // server checks membership against its own account, so send the real one.
    const voiceAccountId = target.source === 'internal' ? PREST0_INC_ACCOUNT_ID : target.accountId;
    // Whether the page has asked for its voice identity, and the URL it was last handed.
    let voiceAsked = false;
    let voiceUrlSent: string | null | undefined;

    // Voice socket identity (L0.2 safe relaunch, 2026-09-05): every voice server refuses
    // anonymous sockets. The page asks; we answer with the signed-in user's Firebase ID
    // token (plus uid, account, UI language and the page credential as a second factor),
    // pinned to the VM origin — never in the iframe URL. The server asks again five
    // minutes before expiry.
    //
    // The three facts are gathered TOGETHER, and the two that are not the token are each
    // bounded (2026-10-08): the page waits for this answer before it opens a socket, and a
    // credential that failed or took its time used to swallow the whole answer, so the page
    // gave up waiting and opened its socket to the workstation with no identity at all.
    const postVoiceAuth = (forceRefresh: boolean): void => {
      const fbUser = getAuth().currentUser;
      const frameWindow = iframeRef.current?.contentWindow;
      if (!fbUser || !frameWindow) return;
      void Promise.all([
        fbUser.getIdTokenResult(forceRefresh),
        // Where the voice socket opens (PR-B): the platform gateway's URL when
        // this account rides it, else null = the workstation, as before.
        voiceAccountId ? voiceUrlWithin(voiceAccountId, VOICE_TRANSPORT_WAIT_MS) : Promise.resolve(null),
        // The page's credential as it is now: a personal ticket where the machine takes
        // one, else the key (2026-09-24/25). The page adopts whatever bsToken arrives
        // here, and the voice server takes a ticket as its second factor only when it
        // names this very person. The platform gateway never needs it.
        withinOr<string | null>(credentialForPage(target), VOICE_CREDENTIAL_WAIT_MS, null),
      ])
        .then(([{ token: idToken, expirationTime }, voiceUrl, bsToken]) => {
          const realExpiry = Date.parse(expirationTime);
          const liveFrame = iframeRef.current?.contentWindow;
          if (!liveFrame) return;
          voiceUrlSent = voiceUrl;
          liveFrame.postMessage(
            {
              source: 'prest0-shell',
              type: 'voiceAuth',
              idToken,
              uid: fbUser.uid,
              accountId: voiceAccountId,
              language: getUserUiLanguage(),
              bsToken,
              expiresAt: Number.isFinite(realExpiry) ? realExpiry : Date.now() + 55 * 60 * 1000,
              voiceUrl,
            },
            allowedOrigin,
          );
        })
        .catch(() => {
          /* not signed in — the server decides */
        });
    };
    // A voice fact that arrives or changes after the page was answered is handed over at once,
    // so the next connect opens where this account's voice now lives.
    const stopHearingVoiceTransport = onVoiceTransportChange((accountId, url) => {
      if (!voiceAsked || accountId !== voiceAccountId || url === voiceUrlSent) return;
      postVoiceAuth(false);
    });

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if ((data as { source?: unknown }).source !== 'preston-realtime') return;
      const type = (data as { type?: unknown }).type;

      // Browser-service auth handshake (2026-08-20 401-wall fix): the VM
      // page cannot read Firestore, so it asks the shell for the per-VM
      // /browser-api bearer and we answer over postMessage, pinned to the
      // VM origin. Never placed in the iframe URL.
      if (type === 'requestBsAuth') {
        // Decided fresh on every ask (2026-09-24; tickets 2026-09-25): the page asks at load,
        // while it waits to launch, and again after the machine refuses a call. It gets a
        // personal ticket where the machine takes one, else the key as it is now.
        void credentialForPage(target).then((token) => {
          const frameWindow = iframeRef.current?.contentWindow;
          if (token && frameWindow) {
            frameWindow.postMessage(
              { source: 'prest0-shell', type: 'bsAuth', token },
              allowedOrigin,
            );
          }
        });
        return;
      }

      // Voice socket identity: answered by postVoiceAuth above.
      if (type === 'requestVoiceAuth') {
        voiceAsked = true;
        postVoiceAuth((event.data as { refresh?: unknown } | null)?.refresh === true);
        return;
      }

      // One window at a time (PR-9c, 2026-09-30): the page's picker names a window of the person's
      // own browser (theirs, or a task's), and it opens by itself in the chat's run window, streamed
      // from the browser, never covered by another window. The page's own machine is named when it
      // is a workstation the run window can be told about; the window vets it again.
      if (type === 'openWindow') {
        const request = readDesktopWindowOpen(data, getUserUiLanguage() === 'es' ? 'es' : 'en');
        if (request) {
          const svc = target.baseUrl ? `${target.baseUrl.replace(/\/+$/, '')}/browser-api` : '';
          requestRunLiveView(/^https:\/\/(?:\d{1,3}-){3}\d{1,3}\.sslip\.io\/browser-api$/i.test(svc) ? { ...request, svc } : request);
        }
        return;
      }

      // Desktop-launch lifecycle → shell status strip (2026-08-09).
      if (type === 'desktopStatus') {
        const raw = (data as { state?: unknown }).state;
        if (
          typeof raw === 'string' &&
          (DESK_STATUS_STATES as readonly string[]).includes(raw)
        ) {
          lastDeskStateRef.current = raw as DeskStatusState;
          setDeskStatus(raw === 'ready' ? null : (raw as Exclude<DeskStatusState, 'ready'>));
        }
        return;
      }

      if (type !== 'contentHeight') return;
      const raw = (data as { height?: unknown }).height;
      if (typeof raw === 'number' && isFinite(raw) && raw > 0) {
        // Clamp to a sane range to guard against runaway values.
        setEmbeddedHeight(Math.min(Math.max(Math.round(raw), 320), 20000));
      } else {
        setEmbeddedHeight(null);
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      stopHearingVoiceTransport();
    };
  }, [target.baseUrl, target.browserServiceToken, target.accountId, target.source]);

  // Hand the page its credential again while it is open (S1b-2c step 2): a ticket lives 15
  // minutes and the page holds only what it is handed, so a fresh one arrives before the old
  // one runs out. The page adopts whatever arrives, the machine key included.
  useEffect(() => {
    const allowedOrigin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    if (!allowedOrigin) return;
    const timer = setInterval(() => {
      void credentialForPage(target).then((token) => {
        const frameWindow = iframeRef.current?.contentWindow;
        if (token && frameWindow) {
          frameWindow.postMessage({ source: 'prest0-shell', type: 'bsAuth', token }, allowedOrigin);
        }
      });
    }, DESKTOP_PAGE_REHAND_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.baseUrl, target.browserServiceToken, target.accountId, target.source]);

  // Reset any embedded height + reported desktop status when the iframe
  // reloads or leaves the ready state, so a fresh load starts clean.
  useEffect(() => {
    setEmbeddedHeight(null);
    setDeskStatus(null);
    lastDeskStateRef.current = null;
  }, [iframeKey, target.targetUrl, target.loading, target.error]);

  // "check again" (2026-08-19, stuck-boot RCA): the old handler was a bare
  // window.location.reload() — a guaranteed NO-OP for every stuck-boot cause
  // (the reload just re-entered the same poll loop). Now it actually acts:
  // an immediate getWorkspaceBootStatus round (which is also the promoter
  // AND runs host discovery server-side) plus a target re-resolve. The
  // iframe key bump keeps the retry semantics for embed failures.
  const bootPollRef = useRef<null | (() => Promise<void>)>(null);
  const handleRetry = () => {
    setIframeKey((k) => k + 1);
    if (bootPollRef.current) {
      void bootPollRef.current();
      target.refresh();
      return;
    }
    // No poller armed (plain embed error) — the soft reload remains the
    // cheapest full re-resolve.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Idle-lifecycle wake (2026-08-18): the asleep-card CTA and the /desktop
  // predictive warm both call the wakeWorkspaceVm function — the same wake
  // seam OS-capability chat turns use — then re-resolve the target so the
  // waking card + boot poller take over.
  // ─────────────────────────────────────────────────────────────────────
  const [wakeBusy, setWakeBusy] = useState(false);
  const requestWake = async (source: 'desktop' | 'button') => {
    if (wakeBusy) return;
    setWakeBusy(true);
    try {
      const fbUser = getAuth().currentUser;
      if (!fbUser) return;
      const token = await fbUser.getIdToken();
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
      await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/wakeWorkspaceVm`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ source }),
        },
      );
      target.refresh();
    } catch {
      /* the asleep card stays up; the user can tap again */
    } finally {
      setWakeBusy(false);
    }
  };

  // Predictive warm: opening /desktop while the workstation sleeps is
  // unambiguous intent — wake it without a click (warmWorkspace latch
  // pattern: once per tab per account; billing sleeps never auto-wake).
  useEffect(() => {
    if (target.lifecycle !== 'asleep' || target.stopReason === 'billing') return;
    if (!target.accountId) return;
    const latchKey = `prest0_desktop_wake_fired_${target.accountId}`;
    try {
      if (sessionStorage.getItem(latchKey)) return;
      sessionStorage.setItem(latchKey, '1');
    } catch { /* storage unavailable — still fire once per mount */ }
    void requestWake('desktop');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.lifecycle, target.stopReason, target.accountId]);

  // ─────────────────────────────────────────────────────────────────────
  // Instant-workspace boot poller (2026-08-02, owner directive).
  //
  // While the account VM reports status 'provisioning' — or a wake is in
  // flight (lifecycle 'waking', 2026-08-18) — poll the
  // getWorkspaceBootStatus function (which is also the health-gated
  // PROMOTER — it flips the routing docs to 'active' the moment both the
  // adapter and the avatar page pass probes). On 'ready', re-resolve the
  // target so the iframe mounts. Server-side probing avoids the CORS wall
  // a direct browser /healthz fetch would hit.
  // ─────────────────────────────────────────────────────────────────────
  const [bootElapsedMin, setBootElapsedMin] = useState(0);
  // Server-authoritative stall flag (getWorkspaceBootStatus → bootStatus.ts
  // WORKSTATION_STALL_MINUTES) so the client and the sweep agree on when a
  // boot has gone wrong rather than each guessing from a local clock.
  const [bootStalled, setBootStalled] = useState(false);
  // Machine-readable stuck-boot diagnosis (2026-08-19): the server's
  // host-discovery / probe outcome, when it has one — drives card copy that
  // says what is actually wrong instead of a generic "taking longer".
  const [bootStallReason, setBootStallReason] = useState<string | null>(null);
  // Adapter is up but browser-service (the desktop renderer) isn't yet —
  // the boot card shows "warming up" instead of mounting a doomed iframe.
  const [desktopWarming, setDesktopWarming] = useState(false);
  // Workstation queue (2026-09-05, workstation plan PR-10): when every desktop
  // slot is a teammate's live session, the chat lane queues the viewer's task
  // (accounts/{id}/workstationQueue, server-written, member-readable) and the
  // 1-minute sweep resumes it as slots free. The tile shows the viewer's OWN
  // ticket — "you're #n" from the position the sweep keeps on the doc, or
  // "your turn" once summoned — instead of an iframe that can only wait.
  // Nothing here decides; a read failure simply shows no card.
  // scaling (PR-13b): the ticket waits on another of the organization's
  // workstations coming up — a wake (~90 s) or a new member (~10 min).
  // Workstation pool strip (2026-09-07, workstation plan PR-13c): for a holder
  // of org.observability.workstations.view (owner by derivation, admin by a
  // deliberate grant), the status row shows the organization's OWN pool —
  // "2 workstations · 1 in use · 1 waking" — from the boot-status endpoint's
  // capacity block, which carries the counts for holders only (the server
  // decides; the client-side key check just spares the poll for everyone
  // else). Counts only; never who is using a machine. Facts, nothing decided.
  const { hasPermission: rbacHas } = useRbac();
  const canSeePool = rbacHas('org.observability.workstations.view');
  const [poolStrip, setPoolStrip] = useState<{ total: number; up: number; waking: number } | null>(null);
  useEffect(() => {
    setPoolStrip(null);
    if (!canSeePool || target.source !== 'account-vm' || !target.accountId || target.lifecycle !== 'active') return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?desktop=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok || cancelled) return;
        const data = await resp.json().catch(() => ({}));
        const cap = data?.capacity && typeof data.capacity === 'object' ? (data.capacity as Record<string, unknown>) : null;
        const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
        const total = cap ? n(cap.membersTotal) : null;
        if (cancelled) return;
        if (total === null || total < 2) { setPoolStrip(null); return; }
        // Facts only: how many are on record, how many are up (serving), how
        // many are waking / being set up. Nothing here is inferred.
        setPoolStrip({ total, up: n(cap!.membersReady) ?? 0, waking: n(cap!.membersWaking) ?? 0 });
      } catch {
        /* a probe blip never hides or invents a strip */
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [canSeePool, target.accountId, target.source, target.lifecycle]);
  /**
   * A SIGN-IN OWED IS A BUTTON, NOT A DEAD END (Miles, 2026-09-20).
   *
   * The incident this closes: a customer was told to come to this page to
   * finish signing into eFax, arrived, and found nothing about eFax on it —
   * because the destination, the pilot, the pointing overlay, the paste
   * bridge and the finish row all live in the connect surface, which only the
   * Integrations tile and a chat card could open. P1 stopped Prest0n sending
   * people here for a sign-in; this makes the page useful to everyone who
   * arrives anyway — after a voice hand-off, from the workspace switcher, or
   * on an old link.
   *
   * ONE strip, never a list: the guidance budget is one directive on screen,
   * and it rides the StatusRow beside the pool and desktop chips rather than
   * inside the Frame, so it cannot cover the live desktop or move the stage
   * (npm run gate:desktop-layout pins that geometry at 14 viewports).
   *
   * Read-only and best-effort: a failed list leaves the strip absent, which
   * is exactly the page as it was before this shipped.
   */
  const [signInOwed, setSignInOwed] = useState<PendingBrowserLaneSignIn | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { listCustomConnections } = await import('../services/composio/ComposioClient');
        const { pendingBrowserLaneSignIns } = await import('../services/composio/browserLaneRoster');
        const owed = pendingBrowserLaneSignIns(await listCustomConnections());
        if (!cancelled) setSignInOwed(owed[0] ?? null);
      } catch {
        /* best-effort — no strip is the pre-2026-09-20 page */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * The click is the SAME door the Integrations tile opens — resumeCustomSignIn
   * starts the thread that picks the journey back up AND opens the surface.
   * Reusing it is the point: two ways to resume a sign-in is how they drift.
   */
  const handleResumeSignIn = useCallback(() => {
    const owed = signInOwed;
    if (!owed) return;
    void import('../services/composio/connectCustomFlow')
      .then((m) => m.resumeCustomSignIn({
        name: owed.name,
        slug: owed.slug,
        signInUrl: owed.signInUrl,
        scope: owed.scope,
      }))
      .catch(() => { /* the surface reports its own failures */ });
  }, [signInOwed]);

  /**
   * The workspace's Windows workstation (docs/PLAN-windows-workstation-20261009.md, W2): where a desktop program such
   * as LexPI runs. The chip opens the same window every other surface opens, beside the owed sign-in and never with
   * it (one directive on screen). The facts come from the platform's own answer, read once a minute at most; any
   * failure leaves the chip absent, which is the page as it was before.
   */
  const workstationAccount = target.source === 'internal'
    ? PREST0_INC_ACCOUNT_ID
    : target.source === 'account-vm' ? target.accountId : null;
  const workstationFacts = useWorkstationFacts(workstationAccount);
  const programWorkstation = useMemo(
    () => programWorkstationEntryOf(workstationFacts, Boolean(signInOwed)),
    [workstationFacts, signInOwed],
  );
  const handleOpenWorkstation = useCallback(() => {
    if (!programWorkstation || !workstationAccount) return;
    openProgramWorkstation({
      program: programWorkstation.program,
      programName: programWorkstation.programName,
      accountId: workstationAccount,
      lang: getUserUiLanguage() === 'es' ? 'es' : 'en',
    });
  }, [programWorkstation, workstationAccount]);

  // The live meter (usage-metering step 11): this person's conversation with Prest0n and the workspace's awake time,
  // for the workspace whose workstation is on screen; its time since the sweep's count runs on while that one is awake.
  const liveMeter = useLiveMeter(workstationAccount, target.lifecycle === 'active' && !target.provisioning);
  const liveMeterShown = liveMeter !== null;

  // The frame's foot carries these chips, so one coming or going can change the frame's own height (and where the
  // chat under it starts); the page's own height keeps the chat off the frame; and a chat that comes to stand under the
  // frame, or leaves, changes the least it keeps there.
  const chatBelowShown = useChatBelowShown();
  chatStandingRef.current = chatBelowShown;
  useEffect(() => { measureRef.current(); }, [embeddedHeight, deskStatus, poolStrip, signInOwed, programWorkstation, liveMeterShown, chatBelowShown]);

  const [queueTicket, setQueueTicket] = useState<{ status: 'queued' | 'summoned'; position: number | null; etaSeconds: number | null; scaling: { action: 'waking' | 'minting'; etaSeconds: number | null } | null } | null>(null);
  useEffect(() => {
    setQueueTicket(null);
    if (target.source !== 'account-vm' || !target.accountId) return;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    let unsub: (() => void) | null = null;
    try {
      const db = getFirestore(getApp());
      const q = query(
        collection(db, ACCOUNT_FIRESTORE_PREFIX, target.accountId, 'workstationQueue'),
        where('actingUid', '==', uid),
      );
      unsub = onSnapshot(
        q,
        (snap) => {
          let mine: { status: 'queued' | 'summoned'; position: number | null; etaSeconds: number | null; scaling: { action: 'waking' | 'minting'; etaSeconds: number | null } | null } | null = null;
          snap.forEach((d) => {
            const t = d.data() as Record<string, unknown>;
            if (t.status !== 'queued' && t.status !== 'summoned') return;
            if (mine && mine.status === 'summoned') return; // a summoned ticket wins: the slot is already ours
            const sc = t.scaling && typeof t.scaling === 'object' ? (t.scaling as Record<string, unknown>) : null;
            mine = {
              status: t.status,
              position: typeof t.position === 'number' ? t.position : null,
              etaSeconds: typeof t.etaSeconds === 'number' ? t.etaSeconds : null,
              scaling: sc && (sc.action === 'waking' || sc.action === 'minting')
                ? { action: sc.action, etaSeconds: typeof sc.etaSeconds === 'number' ? sc.etaSeconds : null }
                : null,
            };
          });
          setQueueTicket(mine);
        },
        () => setQueueTicket(null),
      );
    } catch {
      setQueueTicket(null);
    }
    return () => { if (unsub) unsub(); };
  }, [target.accountId, target.source]);

  // Arrival hold (2026-08-20 eager-org RCA): with eager provisioning, the
  // account is usually promoted to 'active' while the user is still in
  // onboarding — so the provisioning-path desktop hold below never runs and
  // the iframe used to mount straight into a cold/desktop-less VM page. On
  // every arrival at an active account-vm target, hold the iframe behind the
  // warming card until the server's desktop probe answers, failing open 90s
  // from ARRIVAL (never from the long-spent provisioning kickoff).
  const [desktopHold, setDesktopHold] = useState(false);
  useEffect(() => {
    if (
      target.provisioning ||
      target.lifecycle !== 'active' ||
      target.source !== 'account-vm' ||
      !target.targetUrl
    ) {
      setDesktopHold(false);
      return;
    }
    let cancelled = false;
    setDesktopHold(true);
    const arrivalMs = Date.now();
    const release = () => { if (!cancelled) setDesktopHold(false); };
    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - arrivalMs >= 90_000) { release(); return; }
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) { release(); return; }
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?desktop=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) { release(); return; } // fail-open: probe trouble never blocks the desktop
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.desktopReady === false) return; // still warming — hold and re-poll
        release();
      } catch {
        release();
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 5_000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.targetUrl, target.provisioning, target.lifecycle, target.source]);
  useEffect(() => {
    // Re-arm cleanly: a different account (or a fresh boot) must never
    // inherit the previous one's stalled verdict.
    setBootStalled(false);
    setBootStallReason(null);
    setDesktopWarming(false);
    if (!target.provisioning && target.lifecycle !== 'waking' && target.lifecycle !== 'rebuilding') return;
    let cancelled = false;
    const startedMs = target.provisioningStartedAtMs ?? Date.now();
    // Desktop-warming fail-open is measured from when the user is WATCHING,
    // not from the eager kickoff at profile save (2026-08-20 fix — the old
    // 4-minute window was usually spent before anyone arrived).
    const warmupDeadlineMs = Date.now() + 4 * 60_000;

    const tickElapsed = () => {
      if (cancelled) return;
      setBootElapsedMin(Math.max(0, Math.floor((Date.now() - startedMs) / 60000)));
    };
    tickElapsed();
    const elapsedInterval = setInterval(tickElapsed, 30_000);

    const poll = async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?desktop=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) return;
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.state === 'ready') {
          setBootStalled(false);
          // Desktop hold (2026-08-19 terminal-card RCA): "ready" proves the
          // adapter + avatar page, not the browser-service that RENDERS the
          // desktop. Mounting the iframe early burned the VM page's retry
          // ladder against a cold /browser-api and dead-ended on "desktop is
          // taking a moment". Keep the honest boot card up until the server's
          // desktop probe answers — fail-open after 4 minutes so a broken
          // health endpoint can never hold the desktop hostage.
          if (data.desktopReady === false && Date.now() < warmupDeadlineMs) {
            setDesktopWarming(true);
            return; // keep polling; target.refresh() would mount the iframe
          }
          setDesktopWarming(false);
          target.refresh();
          return;
        }
        if (data?.state === 'provisioning' || data?.state === 'waking') {
          setBootStalled(data.stalled === true);
          setBootStallReason(typeof data.stallReason === 'string' ? data.stallReason : null);
        }
        if (data?.state === 'stopped' && (target.lifecycle === 'waking' || target.lifecycle === 'rebuilding')) {
          // The wake fell back to stopped server-side — re-resolve so the
          // asleep card returns instead of an eternal waking spinner.
          target.refresh();
        }
      } catch {
        /* transient — next poll retries */
      }
    };
    // Adaptive cadence (2026-09-09) — see utils/bootPollCadence. A flat 20 s
    // interval meant a machine that finished booting sat behind a "still
    // setting up" card for up to twenty more seconds, which on a first visit
    // is indistinguishable from the boot itself being slow. A self-scheduling
    // timeout instead of setInterval, so the gap can shorten near the moment
    // somebody actually arrived and widen again once the wait is unattended.
    const watchStartedMs = Date.now();
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleNextPoll = () => {
      if (cancelled) return;
      pollTimer = setTimeout(() => {
        void poll().finally(scheduleNextPoll);
      }, bootPollDelayMs(Date.now() - watchStartedMs));
    };
    void poll().finally(scheduleNextPoll);
    // Expose the live poll to "check again" (2026-08-19).
    bootPollRef.current = poll;

    return () => {
      cancelled = true;
      bootPollRef.current = null;
      if (pollTimer) clearTimeout(pollTimer);
      clearInterval(elapsedInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.provisioning, target.provisioningStartedAtMs, target.lifecycle]);

  // ─────────────────────────────────────────────────────────────────────
  // Returning-tab re-resolve (2026-09-03, Miles: the desktop "times out …
  // and goes to a dead screen even if the user leaves and goes to another
  // tab and comes back").
  //
  // The embed owns reconnecting its OWN live view (see the watchdog in
  // preston-realtime.html) — but it cannot fix the one failure that lives
  // out here: while the tab was away the workstation may have slept and
  // woken on a NEW address, so the iframe's src points at a host that no
  // longer answers and no amount of in-page reconnecting can help. Only a
  // re-resolve against the account doc can, and it is one read.
  //
  // Gated on a real absence (60s+) so tabbing away for a moment costs
  // nothing, and deliberately unconditional otherwise: a re-resolve that
  // returns the same target is a no-op, and guessing whether the address
  // changed is exactly the guess that leaves people on a dead frame.
  // ─────────────────────────────────────────────────────────────────────
  const hiddenSinceRef = useRef<number | null>(null);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now();
        return;
      }
      const away = hiddenSinceRef.current ? Date.now() - hiddenSinceRef.current : 0;
      hiddenSinceRef.current = null;
      if (away >= 60_000) target.refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Iframe load watchdog: a refused/failed cross-origin load never fires
  // onLoad and is invisible to React — this converts silence into a
  // visible retry state instead of a dead frame in a pretty card.
  // ─────────────────────────────────────────────────────────────────────
  const [frameStalled, setFrameStalled] = useState(false);
  const frameLoadedRef = useRef(false);
  useEffect(() => {
    if (!target.targetUrl) return;
    frameLoadedRef.current = false;
    setFrameStalled(false);
    const timer = setTimeout(() => {
      if (!frameLoadedRef.current) setFrameStalled(true);
    }, 20_000);
    return () => clearTimeout(timer);
  }, [target.targetUrl, iframeKey]);

  const handleFrameRetry = () => {
    setFrameStalled(false);
    setIframeKey((k) => k + 1);
    target.refresh();
  };

  const onFrameLoad = useCallback(() => { frameLoadedRef.current = true; postShellHello(); }, [postShellHello]);

  // The open chat stands under the frame (the engine says so) while the screen holds both.
  const chatBelow = chatBelowShown && belowHeight !== null;

  return {
    target,
    embedSrc: frameSrc,
    iframeKey,
    bindIframe,
    onFrameLoad,
    frameRef,
    stageHeight: chatBelow ? belowHeight : stageHeight,
    embeddedHeight,
    chatBelow,
    deskStatus,
    poolStrip,
    signInOwed,
    handleResumeSignIn,
    programWorkstation,
    handleOpenWorkstation,
    liveMeter,
    queueTicket,
    desktopHold,
    bootElapsedMin,
    bootStalled,
    bootStallReason,
    desktopWarming,
    frameStalled,
    wakeBusy,
    requestWake,
    handleRetry,
    handleFrameRetry,
  };
}
